import { useState } from 'react';
import { UserCircle, Loader, ShieldQuestion, Phone } from 'lucide-react';
import { ParticipantTabletopState, TabletopCastContact, participantService } from '../../../services/participant.service';

interface Props {
  sessionId: string;
  tabletop: ParticipantTabletopState;
  onSeatClaimed: () => void;
}

// Your seat, your organisation, what you know, what you don't, who to call
// (plan 6.3). When no seat is bound yet, this doubles as the seat-claim UI —
// binding at QR join was judged out of scope for Phase 4 (see progress doc);
// self-service claiming from within the participant view covers the same
// need without touching the join flow.
export function BriefTab({ sessionId, tabletop, onSeatClaimed }: Props) {
  const [claiming, setClaiming] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleClaim = async (personaId: string) => {
    setClaiming(personaId);
    setError(null);
    try {
      await participantService.claimTabletopSeat(sessionId, personaId);
      onSeatClaimed();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setClaiming(null);
    }
  };

  if (!tabletop.seat) {
    return (
      <div className="flex-1 overflow-y-auto p-4 lg:p-0">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-[var(--pt-surface)] rounded-2xl mx-auto mb-4 flex items-center justify-center">
            <UserCircle size={24} className="text-[var(--pt-text-dimmer)]" />
          </div>
          <p className="text-[var(--pt-text-dim)] text-sm font-medium">Choose your seat</p>
          <p className="text-[var(--pt-text-dimmer)] text-xs mt-1">Pick who you're playing in this exercise</p>
        </div>
        {error && <p className="text-red-400 text-xs text-center mb-3">{error}</p>}
        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-2">
          {tabletop.openSeats.map(seat => (
            <button
              key={seat.personaId}
              onClick={() => handleClaim(seat.personaId)}
              disabled={!!claiming}
              className="flex items-center justify-between bg-[var(--pt-surface)] border border-[var(--pt-border)] rounded-2xl px-4 py-3 active:scale-95 transition-transform disabled:opacity-50"
            >
              <div className="text-left">
                <p className="text-[var(--pt-text)] text-sm font-medium">{seat.displayName}</p>
                <p className="text-[var(--pt-text-dimmer)] text-xs">{seat.category.replace(/_/g, ' ')}</p>
              </div>
              {claiming === seat.personaId && <Loader size={16} className="text-primary animate-spin" />}
            </button>
          ))}
          {tabletop.openSeats.length === 0 && (
            <p className="text-[var(--pt-text-dimmer)] text-sm text-center py-8">No open seats right now — ask your facilitator.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 lg:p-0 flex flex-col gap-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[var(--pt-surface)] border border-[var(--pt-border)] rounded-2xl p-4">
          <p className="text-[var(--pt-text-dimmer)] text-xs uppercase tracking-wide">You are playing</p>
          <p className="text-[var(--pt-text)] text-lg font-semibold mt-1">{tabletop.seat.displayName}</p>
          <p className="text-[var(--pt-text-dimmer)] text-xs mt-0.5">{tabletop.seat.category.replace(/_/g, ' ')}</p>
          {tabletop.seat.authority && <p className="text-[var(--pt-text-dim)] text-sm mt-2">{tabletop.seat.authority}</p>}
          {tabletop.actLabel && (
            <p className="text-primary text-xs font-medium mt-3">Current: {tabletop.actLabel}</p>
          )}
        </div>

        {tabletop.coachingCard && (
          <div className="bg-primary/10 border border-primary/30 rounded-2xl p-4">
            <p className="text-primary text-xs uppercase tracking-wide font-semibold mb-2">Your coaching card</p>
            {tabletop.coachingCard.strengths.length > 0 && (
              <div className="mb-2">
                <p className="text-[var(--pt-text-dim)] text-[11px] uppercase tracking-wide">Strengths</p>
                <ul className="mt-1">
                  {tabletop.coachingCard.strengths.map((s, i) => <li key={i} className="text-[var(--pt-text)] text-sm">• {s}</li>)}
                </ul>
              </div>
            )}
            {tabletop.coachingCard.developmentAreas.length > 0 && (
              <div>
                <p className="text-[var(--pt-text-dim)] text-[11px] uppercase tracking-wide">Development areas</p>
                <ul className="mt-1">
                  {tabletop.coachingCard.developmentAreas.map((s, i) => <li key={i} className="text-[var(--pt-text)] text-sm">• {s}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {tabletop.castContacts.length > 0 && <CastSection sessionId={sessionId} contacts={tabletop.castContacts} />}

      {tabletop.brief ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {tabletop.brief.knows.length > 0 && (
            <Section title="What you know" items={tabletop.brief.knows} />
          )}
          {tabletop.brief.suspects.length > 0 && (
            <Section title="What you suspect" items={tabletop.brief.suspects} />
          )}
          {tabletop.brief.wrongBeliefs.length > 0 && (
            <Section
              title="What you believe (unconfirmed)"
              items={tabletop.brief.wrongBeliefs}
              icon={<ShieldQuestion size={13} className="text-amber-400" />}
              className="lg:col-span-2"
            />
          )}
        </div>
      ) : (
        <p className="text-[var(--pt-text-dimmer)] text-sm text-center py-8">Waiting for the exercise to start…</p>
      )}
    </div>
  );
}

const SUMMON_COOLDOWN_MS = 60_000;

// Phone book, not a brief (plan 02, section 8) — one Call button per
// ai_driven persona in the cast, with a client-side cooldown mirrored
// against the backend's own per-persona rate limit so the button doesn't
// just fail silently on a second tap.
function CastSection({ sessionId, contacts }: { sessionId: string; contacts: TabletopCastContact[] }) {
  const [callingId, setCallingId] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);

  const handleCall = async (personaId: string) => {
    setCallingId(personaId);
    setError(null);
    try {
      await participantService.summonTabletopPersona(sessionId, personaId);
      setCooldownUntil(prev => ({ ...prev, [personaId]: Date.now() + SUMMON_COOLDOWN_MS }));
    } catch (err: any) {
      setError(err.message);
      if (err.status === 429) {
        setCooldownUntil(prev => ({ ...prev, [personaId]: Date.now() + SUMMON_COOLDOWN_MS }));
      }
    } finally {
      setCallingId(null);
    }
  };

  return (
    <div className="bg-[var(--pt-surface)] border border-[var(--pt-border)] rounded-2xl p-4">
      <p className="text-[var(--pt-text-dimmer)] text-xs uppercase tracking-wide mb-2">Cast</p>
      {error && <p className="text-red-400 text-xs mb-2">{error}</p>}
      <div className="flex flex-col gap-2">
        {contacts.map(contact => {
          const onCooldown = (cooldownUntil[contact.personaId] || 0) > Date.now();
          return (
            <div key={contact.personaId} className="flex items-center justify-between bg-black/5 rounded-xl px-3 py-2">
              <div className="min-w-0">
                <p className="text-[var(--pt-text)] text-sm font-medium truncate">{contact.displayName}</p>
                {contact.organisation && <p className="text-[var(--pt-text-dimmer)] text-xs truncate">{contact.organisation}</p>}
              </div>
              <button
                onClick={() => handleCall(contact.personaId)}
                disabled={callingId === contact.personaId || onCooldown}
                className="flex items-center gap-1.5 text-xs font-bold text-primary bg-primary/10 rounded-lg px-3 py-1.5 disabled:opacity-40 flex-shrink-0"
              >
                {callingId === contact.personaId ? <Loader size={12} className="animate-spin" /> : <Phone size={12} />}
                {onCooldown ? 'Called' : 'Call'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Section({ title, items, icon, className }: { title: string; items: string[]; icon?: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-[var(--pt-surface)] border border-[var(--pt-border)] rounded-2xl p-4 ${className || ''}`}>
      <div className="flex items-center gap-1.5 mb-2">
        {icon}
        <p className="text-[var(--pt-text-dim)] text-xs uppercase tracking-wide font-medium">{title}</p>
      </div>
      <ul className="flex flex-col gap-1.5">
        {items.map((item, i) => (
          <li key={i} className="text-[var(--pt-text)] text-sm leading-snug">• {item}</li>
        ))}
      </ul>
    </div>
  );
}
